import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { ConfigService } from '../_services/config.service';
import { CompaniesService } from '../companies/services/companies.service';
import { MenuLayoutService } from '../_services/menu-layout.service';
import { BrandingService, CompanyBranding } from '../_services/branding.service';

// il gruppo viene rinfrescato UNA volta per caricamento dell'app (F5/login):
// un cambio di gruppo si applica agli utenti al loro prossimo caricamento
let groupRefreshed = false;

// idem per la posizione del menu (left | top): il valore cacheato in localStorage
// viene applicato subito dal MenuLayoutService, qui lo si riallinea al backend
let menuLayoutRefreshed = false;

// idem per gli sfondi personalizzati (cache in localStorage via BrandingService)
let brandingRefreshed = false;

// l'azienda viene risolta UNA volta per caricamento dell'app: il cambio azienda
// passa sempre da un reload completo (selectCompany/selectPortale), quindi
// rieseguire la fetch ad ogni attivazione di rotta era solo traffico inutile
let companyApplied = false;

// Al logout la SPA NON ricarica la pagina (solo router.navigate), quindi questi
// flag "una volta per load" vanno azzerati esplicitamente: altrimenti il login
// successivo nella stessa tab salta il refresh di gruppo/menu/branding e resta
// con lo stato dell'utente/azienda precedente.
export function resetAuthGuardLoadState(): void {
	groupRefreshed = false;
	menuLayoutRefreshed = false;
	brandingRefreshed = false;
	companyApplied = false;
}

@Injectable({
	providedIn: 'root',
})
export class AuthGuard implements CanActivate {
	constructor(
		private configService: ConfigService,
		private router: Router,
		private companiesService: CompaniesService,
		private menuLayoutService: MenuLayoutService,
		private brandingService: BrandingService,
	) {}

	canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {
		// Check if user is authenticated
		if (!this.configService.isAuthenticated()) {
			// Preserve guid and type if they exist (for password reset/registration flows)
			const guid = localStorage.getItem('guid');
			const type = localStorage.getItem('type');
			// Ricorda l'ultima azienda per ripristinarla al prossimo login
			const cmpMemento = this.configService.getCompanyMemento();

			localStorage.clear();
			console.log('!this.configService.isAuthenticated()', 'login_due_fattori', 'route', route);

			// Restore guid and type after clearing
			if (guid) localStorage.setItem('guid', guid);
			if (type) localStorage.setItem('type', type);
			this.configService.restoreCompanyMemento(cmpMemento);

			// Deep-link chat (?conv=ID, es. dalle email di notifica): ricorda la
			// destinazione e riprendila automaticamente dopo il login.
			// Timestamp incluso: il redirect vale solo per un login immediato (15 min),
			// non per login successivi/di altri utenti sullo stesso browser
			if (state.url && state.url.includes('conv=')) {
				localStorage.setItem('pending_redirect', JSON.stringify({ url: state.url, ts: Date.now() }));
			}

			// Preserve query parameters when redirecting to login
			const queryParams = route.queryParams && Object.keys(route.queryParams).length > 0 ? route.queryParams : {};
			this.router.navigate(['/login_due_fattori'], { queryParams });
			return false;
		}

		// Riprende il deep-link salvato prima del login (una volta sola, entro 15 minuti)
		const pendingRaw = localStorage.getItem('pending_redirect');
		if (pendingRaw) {
			localStorage.removeItem('pending_redirect');
			let pendingUrl = '';
			try {
				const pending = JSON.parse(pendingRaw);
				if (pending?.url && Date.now() - (pending.ts || 0) < 15 * 60 * 1000) {
					pendingUrl = pending.url;
				}
			} catch {
				// formato legacy/corrotto: ignora
			}
			if (pendingUrl && pendingUrl !== state.url) {
				this.router.navigateByUrl(pendingUrl);
				return false;
			}
		}

		return this.setupCompanies();
	}

	private setupCompanies(): Observable<boolean> {
		if (companyApplied) {
			return of(true);
		}

		const cmp_id = this.configService.getCompany_id();

		// Prova a ripristinare l'ultima azienda (cmp_id ricordato); la funzione DB filtra
		// per cmp_id validando l'accesso dell'utente.
		return this.fetchFirstCompany(cmp_id).pipe(
			switchMap((aziende: any[]) => {
				// Azienda ricordata non piu' accessibile (permessi revocati o altro utente):
				// fallback alla prima azienda disponibile.
				if (cmp_id > 0 && (!aziende || aziende.length === 0)) {
					return this.fetchFirstCompany(-1);
				}
				return of(aziende);
			}),
			switchMap((aziende: any[]) => {
				companyApplied = true;
				if (aziende && aziende.length >= 1) {
					return this.applyCompany(aziende[0]);
				}
				// Nessuna azienda disponibile - tema di default
				this.applyDefaultTheme();
				return of(true); // Allow navigation to proceed
			}),
			switchMap((ok: boolean) => this.refreshGroup().pipe(map(() => ok))),
			switchMap((ok: boolean) => this.refreshMenuLayout().pipe(map(() => ok))),
			switchMap((ok: boolean) => this.refreshBranding().pipe(map(() => ok))),
			catchError((error) => {
				console.error('Error loading companies:', error);
				// On error, still allow navigation but with default settings
				this.applyDefaultTheme();
				return of(true);
			}),
		);
	}

	private fetchFirstCompany(cmp_id: number): Observable<any[]> {
		return this.companiesService.GetListCompanies(cmp_id, '', '', 1, 1);
	}

	private applyCompany(azienda: any): Observable<boolean> {
		localStorage.setItem('def_company_name', azienda.name);
		this.configService.setCompany_id(azienda.id);

		const tema = azienda.cmp_tema || 'default';
		document.documentElement.setAttribute('kcs-theme', tema);
		localStorage.setItem('theme', tema);

		// Le liste non trasportano piu' il logo: lo si scarica solo al cambio azienda
		// e lo si tiene in cache in localStorage insieme al cmp_id di appartenenza.
		if (localStorage.getItem('def_company_logo_cmp') === String(azienda.id)) {
			return of(true);
		}
		return this.companiesService.GetCompanyLogo(azienda.id).pipe(
			map((res: any) => {
				const b64 = res?.cmp_logo_b64 || '';
				this.configService.setCompanyLogo(b64 ? 'data:image/png;base64,' + b64 : '');
				localStorage.setItem('def_company_logo_cmp', String(azienda.id));
				return true;
			}),
			catchError(() => {
				this.configService.setCompanyLogo('');
				return of(true);
			}),
		);
	}

	private applyDefaultTheme(): void {
		document.documentElement.setAttribute('kcs-theme', 'default');
		localStorage.setItem('theme', 'default');
	}

	// Rinfresca il gruppo dell'azienda corrente una volta per caricamento dell'app
	private refreshGroup(): Observable<boolean> {
		const cmp_id = this.configService.getCompany_id();
		if (groupRefreshed || cmp_id <= 0) {
			return of(true);
		}
		groupRefreshed = true;
		return this.companiesService.GetCompanyGroup(cmp_id).pipe(
			map((group: string) => {
				this.configService.setCompanyGroup(group);
				return true;
			}),
			catchError(() => of(true)),
		);
	}

	// Rinfresca la posizione del menu dell'azienda corrente una volta per caricamento dell'app
	private refreshMenuLayout(): Observable<boolean> {
		const cmp_id = this.configService.getCompany_id();
		if (menuLayoutRefreshed || cmp_id <= 0) {
			return of(true);
		}
		menuLayoutRefreshed = true;
		return this.companiesService.getMenuLayout(cmp_id).pipe(
			map((layout: string) => {
				this.menuLayoutService.apply(layout);
				return true;
			}),
			catchError(() => of(true)),
		);
	}

	// Rinfresca gli sfondi personalizzati dell'azienda corrente una volta per caricamento dell'app
	private refreshBranding(): Observable<boolean> {
		const cmp_id = this.configService.getCompany_id();
		if (brandingRefreshed || cmp_id <= 0) {
			return of(true);
		}
		brandingRefreshed = true;
		return this.companiesService.getBranding(cmp_id).pipe(
			map((raw: string) => {
				let branding: CompanyBranding = {};
				try {
					branding = raw ? JSON.parse(raw) : {};
				} catch {
					branding = {};
				}
				this.brandingService.apply(branding);
				return true;
			}),
			catchError(() => of(true)),
		);
	}
}
